/* eslint-disable react-hooks/set-state-in-effect */
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { Alert, Box, Button, Checkbox, Divider, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Child } from "../../../models";
import { useGetAllChildrenFilteredQuery } from "../../../redux/apiStore";
import { calculateDateDifference, describeDateDifference } from "../../../utils";
import { ChildAgeAnalysisAppBarSetup } from "./ChildAgeAnalysisAppBarSetup";

interface ScenarioChild {
  id: string;
  name: string;
  birthDate: Dayjs | null;
  included: boolean;
}

interface AgeRow {
  id: string;
  name: string;
  ageDescription: string;
  years: number;
  months: number;
  days: number;
  totalMonths: number;
}

interface AnalysisResult {
  compliant: boolean;
  reasons: string[];
  ageRows: AgeRow[];
}

function getChildName(child: Child): string {
  return `${child.firstName ?? ""} ${child.lastName ?? ""}`.trim();
}

function isEnrolledOnDate(child: Child, scenarioDate: Dayjs): boolean {
  const startDate = child.enrollmentStartCentralDateTime ? dayjs(child.enrollmentStartCentralDateTime) : null;
  const endDate = child.enrollmentEndCentralDateTime ? dayjs(child.enrollmentEndCentralDateTime) : null;

  if (startDate && startDate.isAfter(scenarioDate, "day")) {
    return false;
  }

  if (endDate && endDate.isBefore(scenarioDate, "day")) {
    return false;
  }

  return true;
}

function buildSeedChildren(children: Child[], scenarioDate: Dayjs): ScenarioChild[] {
  return children
    .filter((child) => child.isActive && !child.deleted && isEnrolledOnDate(child, scenarioDate))
    .sort((a, b) => getChildName(a).localeCompare(getChildName(b)))
    .map((child) => ({
      id: `child-${child.id}`,
      name: getChildName(child),
      birthDate: child.birthDate ? dayjs(child.birthDate) : null,
      included: true,
    }));
}

function createEmptyScenarioChild(index: number): ScenarioChild {
  return {
    id: `manual-${Date.now()}-${index}`,
    name: "",
    birthDate: null,
    included: true,
  };
}

function getAnalysisResult(scenarioDate: Dayjs, scenarioChildren: ScenarioChild[]): AnalysisResult {
  const selected = scenarioChildren.filter((entry) => entry.included);
  const reasons: string[] = [];
  const ageRows: AgeRow[] = [];

  if (!selected.length) {
    return {
      compliant: false,
      reasons: ["No children selected for this scenario."],
      ageRows,
    };
  }

  for (const entry of selected) {
    if (!entry.birthDate || !entry.birthDate.isValid()) {
      reasons.push(`${entry.name || "Unnamed child"} is missing a valid birth date.`);
      continue;
    }

    if (entry.birthDate.isAfter(scenarioDate, "day")) {
      reasons.push(`${entry.name || "Unnamed child"} has a birth date after the scenario date.`);
      continue;
    }

    const difference = calculateDateDifference(entry.birthDate.toDate(), scenarioDate.toDate());
    const ageDescription = describeDateDifference(difference);

    ageRows.push({
      id: entry.id,
      name: entry.name || "Unnamed child",
      ageDescription,
      years: difference.years,
      months: difference.months,
      days: difference.days,
      totalMonths: difference.years * 12 + difference.months,
    });
  }

  if (reasons.length) {
    return {
      compliant: false,
      reasons,
      ageRows,
    };
  }

  const childrenUnder12Count = ageRows.filter((row) => row.years < 12).length;
  const childrenUnder5Count = ageRows.filter((row) => row.years < 5).length;
  const childrenUnder24MonthsCount = ageRows.filter((row) => row.totalMonths < 24).length;
  const childrenUnder30MonthsCount = ageRows.filter((row) => row.totalMonths < 30).length;

  if (childrenUnder12Count > 8) {
    reasons.push(`Too many children under age 12: ${childrenUnder12Count} selected (maximum is 8).`);
  }

  const groupingOneCompliant = childrenUnder5Count <= 5 && childrenUnder24MonthsCount <= 3;
  const groupingTwoCompliant = childrenUnder5Count <= 6 && childrenUnder30MonthsCount <= 2;

  if (!groupingOneCompliant && !groupingTwoCompliant) {
    if (childrenUnder5Count > 6) {
      reasons.push(`Too many children under age 5: ${childrenUnder5Count} selected (maximum is 6).`);
    }

    if (childrenUnder24MonthsCount > 3) {
      reasons.push(`Too many children under 24 months for the "5 under age 5" grouping: ${childrenUnder24MonthsCount} selected (maximum is 3).`);
    }

    if (childrenUnder30MonthsCount > 2) {
      reasons.push(`Too many children under 30 months for the "6 under age 5" grouping: ${childrenUnder30MonthsCount} selected (maximum is 2).`);
    }

    if (childrenUnder5Count <= 6 && childrenUnder24MonthsCount <= 3 && childrenUnder30MonthsCount <= 2) {
      reasons.push("Selected age grouping does not match a caregiver-alone compliant pattern.");
    }
  }

  return {
    compliant: reasons.length === 0,
    reasons: reasons.length ? reasons : ["No issues found."],
    ageRows,
  };
}

export function ChildAgeAnalysisPage() {
  const [scenarioDate, setScenarioDate] = useState<Dayjs>(dayjs());
  const [scenarioChildren, setScenarioChildren] = useState<ScenarioChild[]>([]);

  const getChildren = useGetAllChildrenFilteredQuery({ showInactive: true });

  const reloadFromSystemChildren = () => {
    const children = getChildren.data ?? [];
    const seeded = buildSeedChildren(children, scenarioDate);
    setScenarioChildren(seeded.length ? seeded : [createEmptyScenarioChild(0)]);
  };

  const addChild = () => {
    setScenarioChildren((current) => [...current, createEmptyScenarioChild(current.length)]);
  };

  const updateChild = (id: string, updates: Partial<ScenarioChild>) => {
    setScenarioChildren((current) => current.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry)));
  };

  const removeChild = (id: string) => {
    setScenarioChildren((current) => current.filter((entry) => entry.id !== id));
  };

  useEffect(() => {
    if (!getChildren.data?.length) return;
    setScenarioChildren((current) => {
      if (current.length) return current;
      const seeded = buildSeedChildren(getChildren.data ?? [], scenarioDate);
      return seeded.length ? seeded : [createEmptyScenarioChild(0)];
    });
  }, [getChildren.data, scenarioDate]);

  const analysis = useMemo(() => getAnalysisResult(scenarioDate, scenarioChildren), [scenarioDate, scenarioChildren]);

  return (
    <Box sx={{ p: 2 }}>
      <ChildAgeAnalysisAppBarSetup />
      <Stack direction={{ xs: "column", xl: "row" }} gap={2} alignItems="stretch">
        <Paper sx={{ p: 2, flex: 1 }}>
          <Stack gap={2}>
            <Typography variant="h6">Scenario</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ sm: "center" }}>
              <DatePicker
                label="Scenario Date"
                value={scenarioDate}
                onChange={(value) => value && setScenarioDate(value.startOf("day"))}
                slotProps={{ textField: { variant: "filled", sx: { minWidth: 220 } } }}
              />
              <Typography variant="body2" color="text.secondary">
                Ages are calculated as of this date.
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              This scenario evaluates caregiver-alone limits from Illinois DCFS 406.13(a) and 406.13(b).
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              <Button startIcon={<ReplayIcon />} variant="outlined" onClick={reloadFromSystemChildren} sx={{ height: 50 }}>
                Load Active & Enrolled
              </Button>
              <Button startIcon={<AddIcon />} variant="outlined" onClick={addChild} sx={{ height: 50 }}>
                Add Child
              </Button>
            </Stack>

            <Divider />

            <Stack gap={1.5}>
              {scenarioChildren.map((entry) => (
                <Stack key={entry.id} direction="row" gap={1} alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Checkbox checked={entry.included} onChange={(event) => updateChild(entry.id, { included: event.target.checked })} />
                  <TextField
                    label="Child Name"
                    value={entry.name}
                    onChange={(event) => updateChild(entry.id, { name: event.target.value })}
                    variant="filled"
                    size="small"
                    sx={{ flex: 1, minWidth: 110 }}
                  />
                  <DatePicker
                    label="Birth Date"
                    value={entry.birthDate}
                    onChange={(value) => updateChild(entry.id, { birthDate: value ? dayjs(value).startOf("day") : null })}
                    slotProps={{ textField: { variant: "filled", size: "small", sx: { width: { xs: 128, md: 180 } } } }}
                  />
                  <IconButton aria-label="Remove child" onClick={() => removeChild(entry.id)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Stack gap={2}>
            <Typography variant="h6">Scenario Result</Typography>
            <Alert severity={analysis.compliant ? "success" : "error"}>{analysis.compliant ? "Compliant" : "Not Compliant"}</Alert>
            <Stack gap={0.5}>
              {analysis.reasons.map((reason) => (
                <Typography key={reason} variant="body2">
                  • {reason}
                </Typography>
              ))}
            </Stack>
            <Divider />
            <Typography variant="subtitle1">Ages on {scenarioDate.format("M/D/YY")}</Typography>
            <Stack gap={0.75}>
              {analysis.ageRows.map((row) => (
                <Stack key={row.id} direction="row" justifyContent="space-between" gap={2}>
                  <Typography variant="body2">{row.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.ageDescription}
                  </Typography>
                </Stack>
              ))}
              {!analysis.ageRows.length && (
                <Typography variant="body2" color="text.secondary">
                  Add children with valid birth dates to see age results.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
